import { AlertBox } from '@components/ui';
import type { UserInsights } from '@hooks/useInsights';
import { ClipboardList } from 'lucide-react';
import type { ReactElement } from 'react';

interface LegLevelAnalysisProps {
  insights: UserInsights | null;
  minSampleSize: number;
  onMinSampleSizeChange: (value: number) => void;
  isSaving: boolean;
}

export function LegLevelAnalysis({
  insights,
  minSampleSize,
  onMinSampleSizeChange,
  isSaving,
}: LegLevelAnalysisProps): ReactElement {
  const hasData = insights && (insights.totalLegs ?? 0) > 0;

  if (!hasData) {
    return (
      <div>
        <h2 className="text-lg font-bold mb-3 text-white">Performance Analysis (Leg Level)</h2>
        <AlertBox variant="gray" className="mb-4">
          <div className="text-center py-8">
            <ClipboardList
              className="w-16 h-16 text-theme-text-tertiary mx-auto mb-4"
              strokeWidth={1.5}
            />
            <p className="text-theme-text-tertiary text-lg mb-2">No legs tracked yet</p>
            <p className="text-theme-text-tertiary text-sm">
              Track individual legs to see detailed performance by player and prop type.
            </p>
          </div>
        </AlertBox>
      </div>
    );
  }

  const bestPlayers = insights?.bestPlayers || [];
  const worstPlayers = insights?.worstPlayers || [];
  const bestProps = insights?.bestProps || [];
  const worstProps = insights?.worstProps || [];

  return (
    <div>
      <h2 className="text-lg font-bold mb-3 text-white">Your Performance Analysis (Leg Level)</h2>
      <AlertBox variant="blue" className="mb-6">
        <p className="text-sm text-theme-text-secondary">
          <strong className="text-theme-primary">Note:</strong> This analysis is based on{' '}
          <strong>{insights?.totalLegs} tracked legs</strong>. Add more legs in the Leg Tracker tab
          for more accurate insights.
        </p>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-blue-900/50">
          <label className="text-sm text-theme-text-tertiary">Min. bets to appear:</label>
          <select
            value={minSampleSize}
            onChange={(e) => onMinSampleSizeChange(Number(e.target.value))}
            disabled={isSaving}
            className="bg-theme-surface border border-theme-border text-gray-200 text-sm rounded px-2 py-1"
          >
            {[1, 2, 3, 4, 5, 10].map((n) => (
              <option key={n} value={n}>
                {n} bet{n > 1 ? 's' : ''}
              </option>
            ))}
          </select>
          {isSaving && (
            <div className="w-4 h-4 border-2 border-theme-primary border-t-transparent rounded-full animate-spin" />
          )}
          <span className="text-xs text-theme-text-tertiary">
            (higher = more reliable, lower = more visible)
          </span>
        </div>
      </AlertBox>

      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4 mb-4">
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-theme-primary">{insights?.legHitRate}%</div>
          <div className="text-theme-text-tertiary text-sm">Overall Hit Rate</div>
          <div className="text-theme-text-tertiary text-xs">
            {insights?.totalLegHits}/{insights?.totalLegs} legs
          </div>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div
            className={`text-2xl font-bold ${
              parseFloat(insights?.overHitRate || '0') >= 50
                ? 'text-theme-success'
                : 'text-theme-danger'
            }`}
          >
            {insights?.overHitRate}%
          </div>
          <div className="text-theme-text-tertiary text-sm">Overs Hit Rate</div>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div
            className={`text-2xl font-bold ${
              parseFloat(insights?.underHitRate || '0') >= 50
                ? 'text-theme-success'
                : 'text-theme-danger'
            }`}
          >
            {insights?.underHitRate}%
          </div>
          <div className="text-theme-text-tertiary text-sm">Unders Hit Rate</div>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-400">
            {bestPlayers.length + worstPlayers.length}
          </div>
          <div className="text-theme-text-tertiary text-sm">Players Tracked</div>
        </div>
      </div>

      <div className="space-y-4">
        <AlertBox variant="green" title={`Best Performers (${minSampleSize}+ bets)`}>
          {bestPlayers.length > 0 || bestProps.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {bestPlayers.slice(0, 5).map((p, idx) => (
                <li key={idx}>
                  <strong>{p.player}:</strong> {p.hits}/{p.total} ({p.hitRate}% hit rate)
                </li>
              ))}
              {bestProps.slice(0, 3).map((p, idx) => (
                <li key={`prop-${idx}`}>
                  <strong>{p.prop}:</strong> {p.hits}/{p.total} ({p.hitRate}% hit rate)
                </li>
              ))}
            </ul>
          ) : worstPlayers.length > 0 || worstProps.length > 0 ? (
            <p className="text-theme-text-tertiary text-sm">
              All tracked players/props are below 50% hit rate.
            </p>
          ) : (
            <p className="text-theme-text-tertiary text-sm">
              No players/props with {minSampleSize}+ bets yet.
            </p>
          )}
        </AlertBox>

        <AlertBox variant="red" title={`Worst Performers (${minSampleSize}+ bets)`}>
          {worstPlayers.length > 0 || worstProps.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {worstPlayers.slice(0, 5).map((p, idx) => (
                <li key={idx}>
                  <strong>{p.player}:</strong> {p.hits}/{p.total} ({p.hitRate}% hit rate)
                </li>
              ))}
              {worstProps.slice(0, 3).map((p, idx) => (
                <li key={`prop-${idx}`}>
                  <strong>{p.prop}:</strong> {p.hits}/{p.total} ({p.hitRate}% hit rate)
                </li>
              ))}
            </ul>
          ) : bestPlayers.length > 0 || bestProps.length > 0 ? (
            <p className="text-theme-text-tertiary text-sm">
              All tracked players/props are at 50%+ hit rate. Nice!
            </p>
          ) : (
            <p className="text-theme-text-tertiary text-sm">
              No players/props with {minSampleSize}+ bets yet.
            </p>
          )}
        </AlertBox>

        <AlertBox variant="yellow" title="Insights">
          <ul className="space-y-2 text-sm">
            {parseFloat(insights?.overHitRate || '0') >
              parseFloat(insights?.underHitRate || '0') + 10 && (
              <li>
                <strong>You're better at overs:</strong> {insights?.overHitRate}% vs{' '}
                {insights?.underHitRate}% on unders. Consider favoring over bets.
              </li>
            )}
            {parseFloat(insights?.underHitRate || '0') >
              parseFloat(insights?.overHitRate || '0') + 10 && (
              <li>
                <strong>You're better at unders:</strong> {insights?.underHitRate}% vs{' '}
                {insights?.overHitRate}% on overs. Consider favoring under bets.
              </li>
            )}
            {parseFloat(insights?.legHitRate || '0') >= 50 && (
              <li>
                <strong>Solid leg selection:</strong> Your {insights?.legHitRate}% hit rate suggests
                good prop analysis. Focus on reducing parlay size.
              </li>
            )}
            {parseFloat(insights?.legHitRate || '0') < 50 && (insights?.totalLegs ?? 0) >= 10 && (
              <li>
                <strong>Leg selection needs work:</strong> Your {insights?.legHitRate}% hit rate is
                below breakeven. Review your prop research process.
              </li>
            )}
            {(insights?.totalLegs ?? 0) < 50 && (
              <li>
                <strong>Small sample size:</strong> {insights?.totalLegs} legs isn't enough for
                reliable patterns. Keep tracking!
              </li>
            )}
          </ul>
        </AlertBox>
      </div>
    </div>
  );
}
